import { Component, Input, OnChanges, OnDestroy, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { DateAdapter, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin, of, Subscription } from 'rxjs';
import { catchError, finalize, take } from 'rxjs/operators';
import {
  AgronomicConfigurationService,
  EstimatedPhenologyResponse,
  AgronomicProfileResponse,
  AgronomyOption,
  AgronomyOptions,
  PhenologyObservation,
  dateOnlyToLocalDate,
  exclusiveEndToInclusive,
  inclusiveEndToExclusive,
  localDateOnly
} from '@app/core/agronomic-configuration.service';

@Component({
  selector: 'app-agronomic-configuration',
  standalone: false,
  templateUrl: './agronomic-configuration.component.html',
  styleUrls: ['./agronomic-configuration.component.scss'],
  providers: [{ provide: MAT_DATE_LOCALE, useValue: 'es-ES' }]
})
export class AgronomicConfigurationComponent implements OnChanges, OnDestroy {
  @Input() moduleId: number | null = null;
  @ViewChild('deleteObservationDialog') deleteObservationDialog!: TemplateRef<any>;

  public profileForm: FormGroup;
  public observationForm: FormGroup;
  public estimatedPhenologyForm: FormGroup;
  public allOptions: AgronomyOptions | null = null;
  public filteredOptions: AgronomyOptions | null = null;
  public profile: AgronomicProfileResponse | null = null;
  public observations: PhenologyObservation[] = [];
  public estimatedPhenology: EstimatedPhenologyResponse | null = null;
  public estimatedPhenologySaving = false;
  public estimatedPhenologyError = '';
  public loading = false;
  public optionsLoading = false;
  public profileSaving = false;
  public observationSaving = false;
  public profileClosing = false;
  public closingObservationId: number | null = null;
  public deletingObservationId: number | null = null;
  public profileError = '';
  public observationError = '';
  public historyError = '';
  public periodKnown = false;

  private requestSequence = 0;
  private optionsRequestSequence = 0;
  private savedProfileSnapshot = '';
  private dateLocale = 'es-ES';
  private languageSubscription: Subscription;
  private deleteObservationDialogRef?: MatDialogRef<any>;
  private pendingObservationDeleteId: number | null = null;
  private pendingObservationDeleteModuleId: number | null = null;

  constructor(
    private fb: FormBuilder,
    private agronomy: AgronomicConfigurationService,
    private snackBar: MatSnackBar,
    private translate: TranslateService,
    private dateAdapter: DateAdapter<Date>,
    private dialog: MatDialog
  ) {
    const today = dateOnlyToLocalDate(localDateOnly(new Date()));
    this.profileForm = this.fb.group({
      agronomicCropCode: [null],
      varietyId: [null],
      varietyGroupId: [null],
      varietyFreeText: [''],
      validFrom: [today]
    });
    this.observationForm = this.fb.group({
      phaseCodes: [[]],
      observedAt: [today],
      validFrom: [today],
      validThrough: [today]
    });
    this.estimatedPhenologyForm = this.fb.group({ shiftDays: [0], cycleStartDate: [null] });
    this.updateDateLocale(this.translate.currentLang || this.translate.getDefaultLang());
    this.languageSubscription = this.translate.onLangChange.subscribe(event => this.updateDateLocale(event.lang));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['moduleId']) this.loadModule(this.moduleId);
  }

  ngOnDestroy(): void {
    this.languageSubscription?.unsubscribe();
  }

  get cropOptions(): AgronomyOption[] {
    return this.allOptions?.crops || [];
  }

  get varietyOptions(): AgronomyOption[] {
    return this.filteredOptions?.varieties || [];
  }

  get groupOptions(): AgronomyOption[] {
    return this.filteredOptions?.varietyGroups || [];
  }

  get phaseOptions(): AgronomyOption[] {
    return this.filteredOptions?.phases || [];
  }

  get selectablePhenologyOptions(): AgronomyOption[] {
    return this.phaseOptions.filter(option => option.selectable !== false && option.kind !== 'campaign');
  }

  get selectableCampaignOptions(): AgronomyOption[] {
    return this.phaseOptions.filter(option => option.selectable !== false && option.kind === 'campaign');
  }

  get selectedCropCode(): string | null {
    return this.profileForm.get('agronomicCropCode')?.value || null;
  }

  get hasCatalogVarieties(): boolean {
    return this.varietyOptions.length > 0 || this.groupOptions.length > 0;
  }

  get isKnownVarietySelected(): boolean {
    return !!this.profileForm.get('varietyId')?.value;
  }

  get hasProfileChanges(): boolean {
    if (!this.profile || !this.savedProfileSnapshot) return false;
    return this.currentProfileSnapshot() !== this.savedProfileSnapshot;
  }

  get hasIdentityChanges(): boolean {
    if (!this.hasExplicitProfile || !this.profile) return false;
    const value = this.profileForm.getRawValue();
    return (value.agronomicCropCode || null) !== this.profile.identity.agronomicCropCode
      || (value.varietyId || null) !== this.profile.variety.varietyId
      || (value.varietyGroupId || null) !== this.profile.variety.groupId
      || (String(value.varietyFreeText || '').trim() || null) !== this.profile.variety.freeName;
  }

  get hasProfileStartChange(): boolean {
    const storedStart = this.profile?.identity?.validity?.start;
    if (!this.hasExplicitProfile || !storedStart) return false;
    const value = this.profileForm.get('validFrom')?.value;
    const currentStart = value instanceof Date ? localDateOnly(value) : value || null;
    return currentStart !== storedStart;
  }

  get profileSaveBlockReason(): string | null {
    if (!this.hasExplicitProfile || !this.profile) return null;
    if (this.hasIdentityChanges && this.hasProfileStartChange) return 'BALANCE.AGRONOMY.PROFILE_CHANGES_SEPARATE';
    if (this.hasIdentityChanges && localDateOnly(this.profileForm.get('validFrom')?.value) <= (this.profile.identity.validity?.start || '')) {
      return 'BALANCE.AGRONOMY.PROFILE_VERSION_START_MUST_ADVANCE';
    }
    return null;
  }

  get canSaveProfile(): boolean {
    return !!this.moduleId && !!this.selectedCropCode && this.hasProfileChanges && !this.profileSaving && !this.profileSaveBlockReason;
  }

  get canSaveObservation(): boolean {
    return !!this.moduleId && !!this.selectedCropCode &&
      (this.observationForm.get('phaseCodes')?.value || []).length > 0 &&
      !!this.observationForm.get('observedAt')?.value && !this.observationSaving;
  }

  get hasExplicitProfile(): boolean {
    return this.profile?.identity?.status === 'confirmed';
  }

  get hasAnnualCycleStart(): boolean {
    return this.selectedCropCode === 'TOMATO' || this.selectedCropCode === 'MAIZE';
  }

  get canSaveEstimatedPhenology(): boolean {
    if (!this.moduleId || this.estimatedPhenologySaving || this.estimatedPhenology?.region !== 'ES_MEDITERRANEAN_GENERIC') return false;
    const value = this.estimatedPhenologyForm.getRawValue();
    const shift = Number(value.shiftDays);
    if (!Number.isInteger(shift) || Math.abs(shift) > 366) return false;
    const cycleDate = value.cycleStartDate instanceof Date ? localDateOnly(value.cycleStartDate) : value.cycleStartDate || null;
    return shift !== Number(this.estimatedPhenology?.settings?.shiftDays || 0) ||
      cycleDate !== (this.estimatedPhenology?.settings?.cycleStartDate || null);
  }

  displayName(option: AgronomyOption): string {
    return option.displayName || option.label || option.name || option.code || option.id;
  }

  phaseLabel(observation: PhenologyObservation, phaseCode: string): string {
    const phase = (this.allOptions?.phases || []).find(
      item => item.id === phaseCode && item.cropCode === observation.agronomicCropCode
    );
    return phase ? this.translatedPhaseName(phase) : this.translatedPhaseCode(phaseCode);
  }

  get estimatedStageLabel(): string {
    const code = this.estimatedPhenology?.estimate?.primaryStage?.stageCode;
    return code ? this.translatedEstimatedStage(code) : '';
  }

  translatedEstimatedStage(stageCode: string): string {
    const key = `BALANCE.AGRONOMY.ESTIMATE_STAGE_LABELS.${stageCode}`;
    const translated = this.translate.instant(key);
    return translated === key ? stageCode : translated;
  }

  translatedPhaseName(phase: AgronomyOption): string {
    return this.translatedPhaseCode(phase.id, phase.label || phase.name || phase.id);
  }

  formatDateOnly(value: string): string {
    try {
      return new Intl.DateTimeFormat(this.dateLocale, { day: '2-digit', month: '2-digit', year: 'numeric' })
        .format(dateOnlyToLocalDate(value));
    } catch (_error) {
      return value;
    }
  }

  private translatedPhaseCode(phaseCode: string, fallback = phaseCode): string {
    const key = `BALANCE.AGRONOMY.PHASE_LABELS.${phaseCode}`;
    const translated = this.translate.instant(key);
    return translated === key ? fallback : translated;
  }

  groupName(groupId: string | null): string {
    const group = this.groupOptions.find(item => item.id === groupId);
    return group ? this.displayName(group) : '';
  }

  observationPhaseLabels(observation: PhenologyObservation): string {
    return (observation.phaseCodes || [])
      .map(code => this.phaseLabel(observation, code))
      .join(', ');
  }

  observationStatus(observation: PhenologyObservation): string {
    const today = localDateOnly(new Date());
    if (observation.validFrom > today) return 'BALANCE.AGRONOMY.STATUS.FUTURE';
    if (observation.validTo > today) return 'BALANCE.AGRONOMY.STATUS.ACTIVE';
    return 'BALANCE.AGRONOMY.STATUS.FINISHED';
  }

  observationPeriod(observation: PhenologyObservation): string {
    const inclusiveEnd = exclusiveEndToInclusive(observation.validTo);
    return observation.validFrom === inclusiveEnd
      ? this.formatDateOnly(observation.validFrom)
      : `${this.formatDateOnly(observation.validFrom)} – ${this.formatDateOnly(inclusiveEnd)}`;
  }

  canFinishObservation(observation: PhenologyObservation): boolean {
    const today = localDateOnly(new Date());
    const cutoff = observation.validFrom < today
      ? today
      : inclusiveEndToExclusive(observation.validFrom);
    return observation.validFrom <= today && today < observation.validTo && cutoff < observation.validTo;
  }

  onCropChange(cropCode: string | null): void {
    const previousCropCode = this.selectedCropCode;
    this.profileForm.patchValue({
      agronomicCropCode: cropCode,
      varietyId: null,
      varietyGroupId: null,
      varietyFreeText: ''
    });
    this.observationForm.patchValue({ phaseCodes: [] });
    if (previousCropCode && previousCropCode !== cropCode) {
      this.estimatedPhenologyForm.patchValue({ cycleStartDate: null });
    }
    this.filteredOptions = null;
    if (!cropCode) return;
    this.loadFilteredOptions(cropCode);
  }

  onKnownVarietyChange(varietyId: string | null): void {
    if (!varietyId) {
      this.profileForm.patchValue({ varietyGroupId: null });
      return;
    }
    const variety = this.varietyOptions.find(item => item.id === varietyId);
    this.profileForm.patchValue({
      varietyGroupId: variety?.groupId || null,
      varietyFreeText: ''
    });
  }

  onObservedAtChange(date: Date | null): void {
    if (!this.periodKnown || !date) return;
    const dateOnly = localDateOnly(date);
    this.observationForm.patchValue({ validFrom: dateOnlyToLocalDate(dateOnly), validThrough: dateOnlyToLocalDate(dateOnly) });
  }

  setPeriodKnown(known: boolean): void {
    this.periodKnown = known;
    if (known) {
      const observedAt = this.observationForm.get('observedAt')?.value || dateOnlyToLocalDate(localDateOnly(new Date()));
      const dateOnly = localDateOnly(observedAt);
      this.observationForm.patchValue({ validFrom: dateOnlyToLocalDate(dateOnly), validThrough: dateOnlyToLocalDate(dateOnly) });
    }
  }

  saveProfile(): void {
    if (!this.canSaveProfile || !this.moduleId) return;
    const value = this.profileForm.getRawValue();
    this.profileSaving = true;
    this.profileError = '';
    const dateOnly = localDateOnly(value.validFrom);
    const dateOnlyCorrection = this.hasExplicitProfile && !this.hasIdentityChanges;
    const request = dateOnlyCorrection
      ? this.agronomy.correctProfileValidFrom(this.moduleId, dateOnly)
      : this.agronomy.saveProfile(this.moduleId, {
        agronomicCropCode: value.agronomicCropCode,
        varietyId: value.varietyId || null,
        varietyGroupId: value.varietyGroupId || null,
        varietyFreeText: String(value.varietyFreeText || '').trim() || null,
        validFrom: dateOnly
      });
    request.pipe(
      take(1),
      finalize(() => { this.profileSaving = false; })
    ).subscribe({
      next: () => {
        this.showMessage(dateOnlyCorrection ? 'BALANCE.AGRONOMY.PROFILE_VALIDITY_CORRECTED' : 'BALANCE.AGRONOMY.PROFILE_SAVED');
        this.loadModule(this.moduleId);
      },
      error: error => this.setApiError('profile', error)
    });
  }

  closeProfile(): void {
    if (!this.moduleId || !this.hasExplicitProfile || this.profileClosing) return;
    const today = localDateOnly(new Date());
    const start = this.profile?.identity?.validity?.start || today;
    const validTo = start < today ? today : inclusiveEndToExclusive(start);
    this.profileClosing = true;
    this.profileError = '';
    this.agronomy.closeProfile(this.moduleId, validTo).pipe(
      take(1),
      finalize(() => { this.profileClosing = false; })
    ).subscribe({
      next: () => {
        this.showMessage('BALANCE.AGRONOMY.PROFILE_CLOSED');
        this.loadModule(this.moduleId);
      },
      error: error => this.setApiError('profile', error)
    });
  }

  saveObservation(): void {
    if (!this.canSaveObservation || !this.moduleId) return;
    const value = this.observationForm.getRawValue();
    const payload: {
      agronomicCropCode: string;
      phaseCodes: string[];
      observedAt: string;
      validFrom?: string;
      validTo?: string;
    } = {
      agronomicCropCode: this.selectedCropCode!,
      phaseCodes: [...value.phaseCodes],
      observedAt: localDateOnly(value.observedAt)
    };
    if (this.periodKnown) {
      if (!value.validFrom || !value.validThrough) {
        this.observationError = this.translate.instant('BALANCE.AGRONOMY.ERRORS.INVALID_PERIOD');
        return;
      }
      const validFrom = localDateOnly(value.validFrom);
      const validThrough = localDateOnly(value.validThrough);
      if (validThrough < validFrom) {
        this.observationError = this.translate.instant('BALANCE.AGRONOMY.ERRORS.INVALID_PERIOD');
        return;
      }
      if (validFrom < localDateOnly(value.observedAt)) {
        this.observationError = this.translate.instant('BALANCE.AGRONOMY.ERRORS.START_BEFORE_OBSERVATION');
        return;
      }
      payload.validFrom = validFrom;
      payload.validTo = inclusiveEndToExclusive(validThrough);
    }
    this.observationSaving = true;
    this.observationError = '';
    this.agronomy.createObservation(this.moduleId, payload).pipe(
      take(1),
      finalize(() => { this.observationSaving = false; })
    ).subscribe({
      next: () => {
        this.showMessage('BALANCE.AGRONOMY.OBSERVATION_SAVED');
        this.resetObservationForm();
        this.loadObservations(this.moduleId!);
      },
      error: error => this.setApiError('observation', error)
    });
  }

  finishObservation(observation: PhenologyObservation): void {
    if (!this.moduleId || !this.canFinishObservation(observation) || this.closingObservationId !== null) return;
    const today = localDateOnly(new Date());
    const validTo = observation.validFrom < today
      ? today
      : inclusiveEndToExclusive(observation.validFrom);
    this.closingObservationId = observation.idModulePhenologyObservation;
    this.historyError = '';
    this.agronomy.closeObservation(this.moduleId, observation.idModulePhenologyObservation, validTo).pipe(
      take(1),
      finalize(() => { this.closingObservationId = null; })
    ).subscribe({
      next: () => {
        this.showMessage('BALANCE.AGRONOMY.OBSERVATION_FINISHED');
        this.loadObservations(this.moduleId!);
      },
      error: error => this.setApiError('history', error)
    });
  }

  requestDeleteObservation(observation: PhenologyObservation): void {
    if (!this.moduleId || this.closingObservationId !== null || this.deletingObservationId !== null) return;
    this.pendingObservationDeleteId = observation.idModulePhenologyObservation;
    this.pendingObservationDeleteModuleId = this.moduleId;
    this.deleteObservationDialogRef = this.dialog.open(this.deleteObservationDialog, { width: '440px' });
  }

  cancelDeleteObservation(): void {
    this.pendingObservationDeleteId = null;
    this.pendingObservationDeleteModuleId = null;
    this.deleteObservationDialogRef?.close();
    this.deleteObservationDialogRef = undefined;
  }

  confirmDeleteObservation(): void {
    const observationId = this.pendingObservationDeleteId;
    const moduleId = this.pendingObservationDeleteModuleId;
    this.pendingObservationDeleteId = null;
    this.pendingObservationDeleteModuleId = null;
    this.deleteObservationDialogRef?.close();
    this.deleteObservationDialogRef = undefined;
    if (!moduleId || moduleId !== this.moduleId || observationId === null) return;
    this.deletingObservationId = observationId;
    this.historyError = '';
    this.agronomy.deleteObservation(moduleId, observationId).pipe(
      take(1),
      finalize(() => { this.deletingObservationId = null; })
    ).subscribe({
      next: () => {
        this.showMessage('BALANCE.AGRONOMY.OBSERVATION_DELETED');
        this.loadObservations(moduleId);
      },
      error: error => this.setApiError('history', error)
    });
  }

  private loadModule(moduleId: number | null): void {
    if (this.pendingObservationDeleteId !== null) this.cancelDeleteObservation();
    const sequence = ++this.requestSequence;
    ++this.optionsRequestSequence;
    this.optionsLoading = false;
    this.profile = null;
    this.allOptions = null;
    this.filteredOptions = null;
    this.observations = [];
    this.estimatedPhenology = null;
    this.estimatedPhenologyError = '';
    this.profileError = '';
    this.observationError = '';
    this.historyError = '';
    this.savedProfileSnapshot = '';
    if (!moduleId || !Number.isFinite(Number(moduleId))) {
      this.loading = false;
      this.profileForm.reset({
        agronomicCropCode: null,
        varietyId: null,
        varietyGroupId: null,
        varietyFreeText: '',
        validFrom: dateOnlyToLocalDate(localDateOnly(new Date()))
      });
      this.resetObservationForm();
      return;
    }
    this.loading = true;
    forkJoin({
      options: this.agronomy.getOptions().pipe(catchError(error => { this.profileError = this.errorMessage(error); return of(null); })),
      profile: this.agronomy.getProfile(moduleId).pipe(catchError(error => { this.profileError = this.errorMessage(error); return of(null); })),
      history: this.agronomy.getObservations(moduleId).pipe(catchError(error => { this.historyError = this.errorMessage(error); return of(null); }))
    }).pipe(
      take(1),
      finalize(() => { if (sequence === this.requestSequence) this.loading = false; })
    ).subscribe(({ options, profile, history }) => {
      if (sequence !== this.requestSequence) return;
      this.allOptions = options;
      this.profile = profile;
      this.observations = Array.isArray(history?.observations) ? history!.observations : [];
      const identity = profile?.identity;
      const variety = profile?.variety;
      const today = dateOnlyToLocalDate(localDateOnly(new Date()));
      this.profileForm.reset({
        agronomicCropCode: identity?.agronomicCropCode || null,
        varietyId: variety?.varietyId || null,
        varietyGroupId: variety?.groupId || null,
        varietyFreeText: variety?.freeName || '',
        validFrom: identity?.status === 'confirmed' && identity.validity?.start
          ? dateOnlyToLocalDate(identity.validity.start)
          : today
      });
      this.updateSavedSnapshot();
      if (identity?.agronomicCropCode) this.loadFilteredOptions(identity.agronomicCropCode, sequence);
      else this.filteredOptions = options;
      this.resetObservationForm();
      this.loadEstimatedPhenology(moduleId, sequence);
    });
  }

  saveEstimatedPhenology(): void {
    if (!this.moduleId || !this.canSaveEstimatedPhenology) return;
    const value = this.estimatedPhenologyForm.getRawValue();
    const cycleStartDate = value.cycleStartDate instanceof Date ? localDateOnly(value.cycleStartDate) : value.cycleStartDate || null;
    this.estimatedPhenologySaving = true;
    this.estimatedPhenologyError = '';
    this.agronomy.saveEstimatedPhenology(this.moduleId, {
      shiftDays: Number(value.shiftDays),
      cycleStartDate: this.hasAnnualCycleStart ? cycleStartDate : null,
      cycleStartCropCode: this.hasAnnualCycleStart ? this.selectedCropCode : null,
      validFrom: localDateOnly(new Date())
    }).pipe(take(1), finalize(() => { this.estimatedPhenologySaving = false; })).subscribe({
      next: () => {
        this.showMessage('BALANCE.AGRONOMY.ESTIMATE_SAVED');
        this.loadEstimatedPhenology(this.moduleId!);
      },
      error: error => { this.estimatedPhenologyError = this.errorMessage(error); }
    });
  }

  private loadEstimatedPhenology(moduleId: number, expectedSequence = this.requestSequence): void {
    this.agronomy.getEstimatedPhenology(moduleId).pipe(take(1), catchError(error => {
      if (expectedSequence === this.requestSequence && moduleId === this.moduleId) this.estimatedPhenologyError = this.errorMessage(error);
      return of(null);
    })).subscribe(result => {
      if (expectedSequence !== this.requestSequence || moduleId !== this.moduleId || !result) return;
      this.estimatedPhenology = result;
      this.estimatedPhenologyForm.reset({
        shiftDays: Number(result.settings?.shiftDays || 0),
        cycleStartDate: result.settings?.cycleStartDate ? dateOnlyToLocalDate(result.settings.cycleStartDate) : null
      });
    });
  }

  private loadFilteredOptions(cropCode: string, expectedSequence = this.requestSequence): void {
    const optionsSequence = ++this.optionsRequestSequence;
    this.optionsLoading = true;
    this.agronomy.getOptions(cropCode).pipe(
      take(1),
      finalize(() => {
        if (expectedSequence === this.requestSequence && optionsSequence === this.optionsRequestSequence) {
          this.optionsLoading = false;
        }
      }),
      catchError(error => {
        if (expectedSequence === this.requestSequence && optionsSequence === this.optionsRequestSequence) {
          this.profileError = this.errorMessage(error);
        }
        return of(null);
      })
    ).subscribe(options => {
      if (expectedSequence === this.requestSequence && optionsSequence === this.optionsRequestSequence && options) {
        this.filteredOptions = options;
      }
    });
  }

  private loadObservations(moduleId: number): void {
    const sequence = this.requestSequence;
    this.agronomy.getObservations(moduleId).pipe(
      take(1),
      catchError(error => {
        if (sequence === this.requestSequence && moduleId === this.moduleId) this.historyError = this.errorMessage(error);
        return of(null);
      })
    ).subscribe(result => {
      if (sequence === this.requestSequence && moduleId === this.moduleId && result && Array.isArray(result.observations)) {
        this.observations = result.observations;
      }
    });
  }

  private resetObservationForm(): void {
    const today = dateOnlyToLocalDate(localDateOnly(new Date()));
    this.periodKnown = false;
    this.observationForm.reset({ phaseCodes: [], observedAt: today, validFrom: today, validThrough: today });
    this.observationError = '';
  }

  private currentProfileSnapshot(): string {
    const value = this.profileForm.getRawValue();
    return JSON.stringify([
      value.agronomicCropCode || null,
      value.varietyId || null,
      value.varietyGroupId || null,
      String(value.varietyFreeText || '').trim() || null,
      value.validFrom instanceof Date ? localDateOnly(value.validFrom) : value.validFrom || null
    ]);
  }

  private updateSavedSnapshot(): void {
    this.savedProfileSnapshot = this.currentProfileSnapshot();
  }

  private setApiError(section: 'profile' | 'observation' | 'history', error: any): void {
    const message = this.errorMessage(error);
    if (section === 'profile') this.profileError = message;
    else if (section === 'observation') this.observationError = message;
    else this.historyError = message;
  }

  private errorMessage(error: any): string {
    const code = String(error?.error?.error || error?.error?.code || '');
    const category = code.includes('CONFLICT') ? 'CONFLICT'
      : code === 'CLIENT_NOT_ALLOWED' ? 'ACCESS'
      : code === 'MODULE_NOT_FOUND' ? 'NOT_FOUND'
      : code.startsWith('INVALID_') || code.includes('NOT_ALLOWED') || code.includes('MISMATCH') ||
        code.includes('REQUIRES_BOTH') || code.includes('IDENTITY_REQUIRED') || code.includes('EXCLUSIVE') || code.includes('BACKDATED') ? 'INVALID'
      : 'GENERIC';
    return this.translate.instant(`BALANCE.AGRONOMY.ERRORS.${category}`, { code });
  }

  private showMessage(key: string): void {
    this.snackBar.open(
      this.translate.instant(key),
      this.translate.instant('COMMON.CLOSE'),
      { duration: 3500 }
    );
  }

  private updateDateLocale(language: string): void {
    const normalized = String(language || 'es-ES').toLowerCase();
    this.dateLocale = normalized.startsWith('en') ? 'en-US' : normalized.startsWith('fr') ? 'fr-FR' : 'es-ES';
    this.dateAdapter.setLocale(this.dateLocale);
  }
}
